/**
 * ReportListCard.tsx
 *
 * Renders the `report_list` envelope data emitted by onething_list_reports
 * and onething_team_reports. Summary line (count / date-range header) above
 * compact rows.
 */

import { FileText, Inbox, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// ---------------------------------------------------------------------------
// Data shape emitted by the BE envelope for kind "report_list"
// ---------------------------------------------------------------------------

export interface ReportListRow {
  pid: string;
  reportDate: string;
  userName: string;
  priorityCode: string;
  blocker: boolean;
  content: string;
}

// ---------------------------------------------------------------------------
// Single row
// ---------------------------------------------------------------------------

const MAX_PREVIEW = 120;

function ReportRow({ row }: { row: ReportListRow }) {
  const preview =
    row.content.length > MAX_PREVIEW
      ? row.content.slice(0, MAX_PREVIEW).trimEnd() + "…"
      : row.content;

  return (
    <div className="mx-1 rounded-lg px-2 py-2.5 transition-colors hover:bg-muted/50 cursor-default">
      {/* Meta line */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">{row.reportDate}</span>
        <span className="text-[12px] font-semibold tracking-tight">{row.userName}</span>
        <Badge variant="outline" className="rounded-full font-mono text-[10.5px] px-2 py-0.5">
          {row.priorityCode}
        </Badge>
        {row.blocker && (
          <Badge
            variant="outline"
            className="gap-1 rounded-full text-[10.5px] font-semibold px-2 py-0.5 bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
          >
            <AlertTriangle className="h-2.5 w-2.5" />
            Blocker
          </Badge>
        )}
      </div>
      {/* Content preview */}
      <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{preview}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface ReportListCardProps {
  summary: string;
  rows: ReportListRow[];
}

export function ReportListCard({ summary, rows }: ReportListCardProps) {
  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-2.5 px-4 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-3.5 w-3.5" />
        </div>
        <p className="text-[13.5px] font-bold leading-tight tracking-tight flex-1 min-w-0">
          {summary}
        </p>
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
          {rows.length}
        </span>
      </CardHeader>
      <CardContent className="px-2 pb-2 pt-0">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-50" />
            <span className="text-xs">No reports found.</span>
          </div>
        ) : (
          <div>
            {rows.map((row) => (
              <ReportRow key={row.pid} row={row} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
